<script setup lang="ts">
import { $getRoot } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onUnmounted, ref } from "vue";

import { fillNodeWithMarkdown } from "~/components/text/transformers";

const emit = defineEmits<{
  updated: [];
}>();

const currentText = ref("");
const targetText = ref("");
const currentIndex = ref(0);
const isStreaming = ref(false);
let streamInterval: NodeJS.Timeout | null = null;

const editor = useLexicalComposer();
const updateEditor = (text: string) => {
  editor.update(() => {
    const root = $getRoot();
    root.clear();
    fillNodeWithMarkdown(root, text);
    emit("updated");
  });
};

const getStreamingConfig = (): { speedMs: number; charsPerStep: number } => {
  const remaining = targetText.value.length - currentIndex.value;
  switch (true) {
    case remaining > 500: {
      return { speedMs: 0.025, charsPerStep: 80 };
    }
    case remaining > 200: {
      return { speedMs: 0.25, charsPerStep: 20 };
    }
    case remaining > 100: {
      return { speedMs: 0.5, charsPerStep: 5 };
    }
    case remaining > 50: {
      return { speedMs: 1, charsPerStep: 2 };
    }
    default: {
      return { speedMs: 2, charsPerStep: 1 };
    }
  }
};

const startStreaming = () => {
  if (streamInterval) {
    clearInterval(streamInterval);
  }

  isStreaming.value = true;
  const streamStep = (): void => {
    if (currentIndex.value >= targetText.value.length && streamInterval) {
      clearInterval(streamInterval);
      streamInterval = null;
      isStreaming.value = false;
      return;
    }

    const config = getStreamingConfig();
    currentIndex.value = Math.min(currentIndex.value + config.charsPerStep, targetText.value.length);
    currentText.value = targetText.value.slice(0, currentIndex.value);
    updateEditor(currentText.value);

    if (streamInterval) {
      clearInterval(streamInterval);
      streamInterval = setInterval(streamStep, config.speedMs);
    }
  };

  streamInterval = setInterval(streamStep, getStreamingConfig().speedMs);
};

const replaceAll = (markdown: string) => {
  const isIncremental = markdown.startsWith(targetText.value) && markdown.length >= targetText.value.length;
  targetText.value = markdown;

  if (isIncremental) {
    if (!streamInterval && currentIndex.value < targetText.value.length) {
      startStreaming();
    }
  } else {
    currentIndex.value = 0;
    currentText.value = "";
    startStreaming();
  }
};

onUnmounted(() => {
  if (streamInterval) {
    clearInterval(streamInterval);
    isStreaming.value = false;
  }
});

defineExpose({
  replaceAll,
  isStreaming,
});
</script>

<template>
  <slot />
</template>
